import React from 'react';
import './UserAvatar.css';

// Distinct palette so each user in a shared list gets a recognizable color.
const AVATAR_COLORS = [
  '#667eea', // Purple
  '#f093fb', // Pink
  '#4facfe', // Blue
  '#43e97b', // Green
  '#fa709a', // Rose
  '#feca57', // Yellow
  '#ff6348', // Orange-Red
  '#00d2d3', // Cyan
];

// Pick a stable color for a user: keyed on userId when available (so the same person is
// always the same color everywhere), otherwise a hash of their name as a fallback.
function getAvatarColor(user) {
  let key = user.userId;
  if (key === undefined || key === null) {
    key = 0;
    for (let i = 0; i < user.name.length; i++) {
      key = (key * 31 + user.name.charCodeAt(i)) | 0;
    }
  }
  return AVATAR_COLORS[Math.abs(key) % AVATAR_COLORS.length];
}

/**
 * UserAvatar component displays a circular avatar with user's initials
 * @param {Object} props
 * @param {Object} props.user - User object with userId and name
 * @param {number} props.user.userId - User's ID
 * @param {string} props.user.name - User's full name
 * @param {string} [props.size='small'] - Size of avatar: 'small' or 'medium'
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.label='Created by'] - Accessible label prefix (e.g. 'Assigned to')
 */
export default function UserAvatar({ user, size = 'small', className = '', label = 'Created by' }) {
  if (!user || !user.name) {
    return null;
  }

  // Extract initials from name
  const getInitials = (name) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      // Single name: use first letter
      return parts[0].charAt(0).toUpperCase();
    }
    // Multiple parts: use first letter of first and last name
    const firstInitial = parts[0].charAt(0).toUpperCase();
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  };

  // Format name for tooltip (first name + first letter of last name)
  const getDisplayName = (name) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0];
    }
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}.`;
  };

  const initials = getInitials(user.name);
  const displayName = getDisplayName(user.name);

  return (
    <div
      className={`user-avatar user-avatar-${size} ${className}`}
      style={{ backgroundColor: getAvatarColor(user) }}
      title={`${label}: ${displayName}`}
      aria-label={`${label} ${displayName}`}
    >
      {initials}
    </div>
  );
}


